/**
 * collov_* a2ui `metadata.extensions` RICH renderers (session surface).
 *
 * a2ui 1.0 lets a component carry `metadata.extensions.<vendor>_<name>` payloads
 * that conformant renderers MUST ignore when unknown. The relayed envelope always
 * carries BOTH the recognised extension AND the expanded core child tree (a
 * third-party renderer draws the core children; OUR kit draws the rich layout).
 *
 * The FOUR shaped extensions in v1 (the rest of the 14 legacy customs are plain
 * core compositions — lists, chips=Buttons, media=Image/Video bindings):
 *   collov_shopping_list  (host List)      → buy cards with an openUrl Button
 *   collov_stat_grid      (host Row/List)  → a grid of value/label stat cards
 *   collov_before_after   (host Row)       → an interactive before/after slider
 *   collov_table          (host Column)    → a header + rows table
 *
 * Recognition lives in collovExtensionMatch.js (`recognisedCollovExtension`);
 * this file exports only the renderer component (react-refresh constraint).
 */
import { useState } from 'react';

const MUTED = 'var(--vi-text-muted, #8E8E93)';
const HAIR = 'var(--vi-hair, #ECECEC)';
const SURFACE = 'var(--vi-surface, #fff)';

function cellText(v) {
  if (v == null) return '';
  if (typeof v === 'object') return v.text ?? v.value ?? v.label ?? '';
  return String(v);
}
function priceText(v) {
  if (v == null) return '';
  if (typeof v === 'number') return `$${v.toFixed(2)}`;
  return String(v);
}

const CARD = {
  display: 'flex', flexDirection: 'column', gap: 6, padding: 12, borderRadius: 14,
  background: SURFACE, border: `1px solid ${HAIR}`, boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

function CollovShoppingList({ payload, onOpenUrl, onOpenImage }) {
  const items = Array.isArray(payload.items) ? payload.items : [];
  return (
    <div data-collov-shopping-list style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it, i) => (
        <div key={i} data-collov-shopping-item style={CARD}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {it.image ? (
              <img
                src={it.image} alt={it.name || ''} onClick={() => onOpenImage?.(it.image)}
                style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', cursor: 'zoom-in', flex: '0 0 auto' }}
              />
            ) : null}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{it.name}</div>
              {(it.brand || it.qty) ? (
                <div style={{ fontSize: 12.5, color: MUTED }}>{[it.brand, it.qty].filter(Boolean).join(' · ')}</div>
              ) : null}
              {it.price != null ? <div style={{ fontSize: 14, fontWeight: 600 }}>{priceText(it.price)}</div> : null}
            </div>
            {it.buyUrl ? (
              <button
                type="button" data-collov-buy onClick={() => onOpenUrl?.(it.buyUrl)}
                style={{
                  appearance: 'none', border: 0, borderRadius: 999, padding: '8px 16px',
                  background: 'var(--vi-accent, #111)', color: '#fff', font: 'inherit', cursor: 'pointer', flex: '0 0 auto',
                }}
              >
                Buy
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function CollovStatGrid({ payload }) {
  const stats = Array.isArray(payload.stats) ? payload.stats
    : (Array.isArray(payload.items) ? payload.items : []);
  const cols = Math.min(Math.max(stats.length, 1), 4);
  return (
    <div data-collov-stat-grid style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 10 }}>
      {stats.map((s, i) => (
        <div key={i} style={{ ...CARD, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{cellText(s.value ?? s)}</div>
          {s && typeof s === 'object' && s.label != null
            ? <div style={{ fontSize: 12, color: MUTED }}>{cellText(s.label)}</div> : null}
        </div>
      ))}
    </div>
  );
}

function CollovBeforeAfter({ payload }) {
  const [pos, setPos] = useState(50);
  const before = payload.before || {};
  const after = payload.after || {};
  const beforeUrl = before.url || payload.beforeUrl;
  const afterUrl = after.url || payload.afterUrl;
  return (
    <div data-collov-before-after>
      <div style={{
        position: 'relative', width: '100%', aspectRatio: payload.ratio || '4/3',
        borderRadius: 12, overflow: 'hidden', background: '#000',
      }}>
        {afterUrl ? (
          <img src={afterUrl} alt={after.label || 'after'}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : null}
        {beforeUrl ? (
          <img src={beforeUrl} alt={before.label || 'before'}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
              clipPath: `inset(0 ${100 - pos}% 0 0)`,
            }} />
        ) : null}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, width: 2, background: '#fff', transform: 'translateX(-1px)' }} />
      </div>
      <input
        type="range" min={0} max={100} value={pos} aria-label="before after compare"
        onChange={(e) => setPos(Number(e.target.value))} style={{ width: '100%', marginTop: 6 }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: MUTED }}>
        <span>{before.label || 'Before'}</span>
        <span>{after.label || 'After'}</span>
      </div>
    </div>
  );
}

function CollovTable({ payload }) {
  const columns = Array.isArray(payload.columns) ? payload.columns
    : (Array.isArray(payload.headers) ? payload.headers : []);
  const rows = Array.isArray(payload.rows) ? payload.rows : [];
  const th = { textAlign: 'left', fontSize: 12, fontWeight: 600, color: MUTED, padding: '6px 10px', borderBottom: `1px solid ${HAIR}` };
  const td = { fontSize: 14, padding: '6px 10px', borderBottom: `1px solid ${HAIR}` };
  return (
    <div data-collov-table style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        {columns.length ? (
          <thead><tr>{columns.map((c, i) => <th key={i} style={th}>{cellText(c)}</th>)}</tr></thead>
        ) : null}
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {(Array.isArray(r) ? r : [r]).map((cell, j) => <td key={j} style={td}>{cellText(cell)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Render the rich layout for a recognised `collov_*` extension. `onOpenUrl` opens
 * a buy link through the host (the session surface routes it to `open_url`);
 * `onOpenImage` opens the full-screen viewer.
 */
export function CollovExtensionHost({ extKey, payload, onOpenUrl, onOpenImage }) {
  if (extKey === 'collov_shopping_list') return <CollovShoppingList payload={payload} onOpenUrl={onOpenUrl} onOpenImage={onOpenImage} />;
  if (extKey === 'collov_stat_grid') return <CollovStatGrid payload={payload} />;
  if (extKey === 'collov_before_after') return <CollovBeforeAfter payload={payload} />;
  if (extKey === 'collov_table') return <CollovTable payload={payload} />;
  return null;
}
